/*
 * Vehicle picker list helpers: which years, makes and models the drawer offers, read straight from
 * the fitment index of the product chosen in Type (SeatFit.loadIndex(kind)), and the product URL the
 * GO button opens. No DOM here, so the drawer script only deals with behaviour and these rules can be
 * checked on their own. Loaded `defer` by sections/seatfit-vehicle-picker.liquid before seatfit-vehicle-picker.js;
 * the homepage bar and the "Other products" block load it too.
 */
(function () {
  'use strict';
  if (window.SeatFitPickerLists) return; // the section may be rendered twice (editor preview, re-render)

  // Only ad/click ids and the product look (?style=diamond, from an ad aimed at one Style) travel from the landing
  // page to the product page, so attribution and the look survive the jump while stale vehicle params (body, set,
  // seat, variant) from an earlier visit never do.
  var KEEP_PARAM = /^(utm_[a-z0-9_]+|fbclid|gclid|style)$/i;
  // Kept only when GO returns to the same product: a colour slug means nothing on another product (a seat colour on
  // the car-cover page would steer its price tier), but Edit Vehicle on the same page must keep the chosen colour.
  var SAME_PRODUCT_PARAM = /^color$/i;
  // A range wider than this is a data error; skipping it keeps one bad row from freezing the page.
  var MAX_SPAN = 200;

  function slug(v) {
    var SF = window.SeatFit;
    return SF && SF.slugify ? SF.slugify(v) : String(v == null ? '' : v).toLowerCase();
  }

  // Natural order ("3 Series" before "10 Series", "F-150" before "F-250"), case-insensitive.
  function byLabel(a, b) {
    return String(a.label).localeCompare(String(b.label), 'en', { numeric: true, sensitivity: 'base' });
  }

  function saneRange(r) {
    return Array.isArray(r) && r[0] > 0 && r[1] >= r[0] && r[1] - r[0] <= MAX_SPAN;
  }

  function covers(model, year) {
    return (model.y || []).some(function (r) { return saneRange(r) && r[0] <= year && year <= r[1]; });
  }

  function makesOf(index) { return (index && Array.isArray(index.makes)) ? index.makes : []; }

  // Every year at least one model is sold for, newest first (the competitor lists years descending).
  function years(index) {
    var seen = {};
    makesOf(index).forEach(function (mk) {
      (mk.models || []).forEach(function (m) {
        (m.y || []).forEach(function (r) {
          if (!saneRange(r)) return;
          for (var y = r[0]; y <= r[1]; y++) seen[y] = true;
        });
      });
    });
    return Object.keys(seen).map(Number).sort(function (a, b) { return b - a; })
      .map(function (y) { return { value: String(y), label: String(y) }; });
  }

  function makes(index, year) {
    if (!year) return [];
    return makesOf(index)
      .filter(function (mk) { return (mk.models || []).some(function (m) { return covers(m, year); }); })
      .map(function (mk) { return { value: mk.s, label: mk.n }; })
      .sort(byLabel);
  }

  function models(index, year, makeSlug) {
    if (!year || !makeSlug) return [];
    var mk = null;
    makesOf(index).some(function (m) { return m.s === makeSlug ? (mk = m) : false; });
    return (mk ? mk.models || [] : [])
      .filter(function (m) { return covers(m, year); })
      .map(function (m) { return { value: m.s, label: m.n }; })
      .sort(byLabel);
  }

  // Prefill may carry a slug (from the URL) or a display name (make=Mercedes-Benz in an ad link).
  function match(options, wanted) {
    if (wanted == null || wanted === '') return null;
    var w = slug(wanted);
    for (var i = 0; i < options.length; i++) {
      if (options[i].value === String(wanted) || slug(options[i].value) === w || slug(options[i].label) === w) return options[i].value;
    }
    return null;
  }

  // Applies a waiting prefill and drops later choices the earlier ones no longer offer.
  // Make/model of a prefill without a usable year stay pending until the shopper picks a year,
  // so a "Choose your F-150" card still lands on F-150 once a year is chosen. A year the list lacks
  // goes too: switching Type loads another product's list, which may not reach back that far.
  function settle(index, pick, pending) {
    var p = pending ? Object.assign({}, pending) : null;
    var out = { year: pick.year, make: pick.make, model: pick.model };
    if (p && p.year != null && p.year !== '') { out.year = match(years(index), p.year); p.year = null; }
    else if (out.year) out.year = match(years(index), out.year);
    var y = out.year ? Number(out.year) : null;
    if (p && y) {
      if (p.make) out.make = match(makes(index, y), p.make) || out.make;
      if (out.make && p.model) out.model = match(models(index, y, out.make), p.model) || null;
      p = null;
    }
    if (!y || !match(makes(index, y), out.make)) out.make = null;
    if (!out.make || !match(models(index, y, out.make), out.model)) out.model = null;
    return { pick: out, pending: p };
  }

  // Per field: options, current value and whether it can be used yet (grey + disabled when not).
  // typeValue: the product kind the Type field shows (its first option when not given).
  function fieldStates(index, pick, typeOptions, typeValue) {
    var y = pick.year ? Number(pick.year) : null;
    var firstType = typeOptions && typeOptions[0] ? typeOptions[0].value : null;
    var st = {
      type: { options: typeOptions || [], value: match(typeOptions || [], typeValue) || firstType, ok: true },
      year: { options: index ? years(index) : [], value: pick.year, ok: !!index },
      make: { options: index ? makes(index, y) : [], value: pick.make, ok: !!(index && y) },
      model: { options: index ? models(index, y, pick.make) : [], value: pick.model, ok: !!(index && y && pick.make) }
    };
    var next = false; // the first usable empty field is outlined red, like the competitor
    ['type', 'year', 'make', 'model'].forEach(function (k) {
      var f = st[k];
      // With the Type row switched off there is no type value; it must not take the red "next"
      // outline, or Year (and every later field) would render grey as if disabled.
      if (k === 'type' && !f.options.length) { f.state = 'done'; return; }
      f.state = f.value ? 'done' : (f.ok && !next ? (next = 'next') : 'off');
    });
    return st;
  }

  // GO target: the product page with the vehicle as slugs (?year=2020&make=toyota&model=camry, the
  // shape the product page reads back), plus the kept params above.
  function goUrl(productUrl, pick, currentHref) {
    var url = new URL(productUrl || '/', location.origin);
    url.searchParams.set('year', String(pick.year));
    url.searchParams.set('make', slug(pick.make));
    url.searchParams.set('model', slug(pick.model));
    var same = false;
    try {
      var here = new URL(currentHref || location.href);
      same = here.pathname.replace(/\/+$/, '') === url.pathname.replace(/\/+$/, '');
      here.searchParams.forEach(function (v, k) {
        var keep = KEEP_PARAM.test(k) || (same && SAME_PRODUCT_PARAM.test(k));
        if (keep && !url.searchParams.has(k)) url.searchParams.append(k, v);
      });
    } catch (e) { console.warn('[SeatFit picker] could not read tracking params', e); }
    // A look reached without ?style= (an ad's ?variant=<Diamond id>) lives only in html[data-sf-style], which only a
    // product with a Style option sets: carried back to that same product so Edit Vehicle keeps the shopper on it.
    var root = typeof document !== 'undefined' ? document.documentElement : null;
    var look = root && root.dataset ? root.dataset.sfStyle : '';
    if (same && look && !url.searchParams.has('style')) url.searchParams.set('style', look);
    return url.pathname + url.search;
  }

  // The drawer only needs the core file, but waits for 'seatfit:ready' when it runs first
  // (e.g. a section re-rendered in the editor before the core finished loading).
  function whenReady() {
    return new Promise(function (resolve) {
      if (window.SeatFit && window.SeatFit.loadIndex) return resolve(window.SeatFit);
      document.addEventListener('seatfit:ready', function () { resolve(window.SeatFit); }, { once: true });
    });
  }

  // Index or a rejection: a stalled request must end in "try again", not an endless spinner.
  // kind: whose vehicle list ('seat' | 'cover' | 'mat'); missing = the product this page sells.
  function fetchIndex(timeoutMs, kind) {
    var timeout = new Promise(function (resolve, reject) {
      setTimeout(function () { reject(new Error('fitment index timed out')); }, timeoutMs);
    });
    return Promise.race([whenReady().then(function (SF) { return SF.loadIndex(kind); }), timeout]).then(function (idx) {
      if (!idx || !Array.isArray(idx.makes) || !idx.makes.length) throw new Error('empty or missing fitment index');
      return idx;
    });
  }

  window.SeatFitPickerLists = {
    fetchIndex: fetchIndex,
    years: years, makes: makes, models: models, match: match, settle: settle, fieldStates: fieldStates,
    goUrl: goUrl, whenReady: whenReady, byLabel: byLabel
  };
})();
